import React, { useRef, useMemo, useState, useEffect } from "react";
import { Icon } from "@rsuite/icons";
import { Save, ImportStorage } from "@rsuite/icons";
import { useData } from "hooks/useData";
import { filesMock } from "mocks/filesMock";
import { handleDownload } from "utils";
import { useSearchParams } from "react-router-dom";
import { useGetFilesQuery, useDownloadFileMutation } from "store/apiSlice";
import { FileFilters, filesFormModel } from "types/filterConfig";
import FilesFilters from "./FilesFilters";
import DarkTable from "components/Table/DarkTable";
import Panel from "components/base/Panel";
import {
    PageContainer
} from "components/Page/PageComponents";

import TableActionButton from "components/Button/TableActionButton";
import { endOfDayUTC, parseBackendUTC, startOfDayUTC, toBackendUTCString } from "utils/time";


interface FilesPageProps {
    initFilters: FileFilters
};

const FilesPage: React.FC<FilesPageProps> = ({ initFilters }) => {
    const [refreshKey, setRefreshKey] = useState(Date.now());
    const [searchParams, setSearchParams] = useSearchParams();
    const [downloadingKeys, setDownloadingKeys] = useState<Set<number>>(new Set());
    const currentParamsRef = useRef(new URLSearchParams(searchParams.toString()));

    const [formError, setFormError] = useState<Record<string, string>>({});

    const filterKeys: (keyof FileFilters)[] = [
        "time_from",
        "time_to",
        "source"
    ];

    const notUpdatableKeys: string[] = ["time_from", "time_to"];

    const savedFilters = useMemo<FileFilters>(() => {
        const filters: FileFilters = { ...initFilters };

        filterKeys.forEach((key) => {
            const value = searchParams.get(key);
            if (value !== null && !notUpdatableKeys.includes(key)) {
                filters[key] = value;
            }
        });

        const validation = filesFormModel.check(filters);
        const newErrors: Record<string, string> = {};

        Object.entries(validation).forEach(([key, val]: any) => {
            if (val.hasError && !notUpdatableKeys.includes(key)) {
                newErrors[key] = val.errorMessage;
            }
        });

        setFormError(newErrors);

        return filters;
    }, [initFilters]);

    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        let mutated = false;

        for (const key of filterKeys) {
            if (!params.has(key) && initFilters[key] !== undefined && !notUpdatableKeys.includes(key)) {
                params.set(key, String(initFilters[key] ?? ""));
                mutated = true;
            }
        }

        if (mutated) setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [filters, setFilters] = useState<FileFilters>(savedFilters);
    const {
        data: files,
        isLoading,
        isFetching,
        error
    } = useData(
        useGetFilesQuery,
        filesMock,
        {
            time_from: filters.time_from,
            time_to: filters.time_to,
            source: filters.source,
            refreshKey
        },
        filters === initFilters
    );

    const [downloadFile] = useDownloadFileMutation();

    // "key": 743396,
    // "original_filename": "lexico-195.219.39.9.pcap597963",
    //  "client_ip": "195.219.39.9",
    //  "source_from_client": "",
    //  "source": "195.219.39.9",
    //  "date_first": "2025-09-03T13:32:22.474902",
    //  "date_last": "2025-09-03T13:32:29.533171",
    //  "uploaded_at": "2025-09-03T13:33:34.331000",
    //  "packets": 29904,
    //  "calls": 6213

    const columns = useMemo(() => [
        {
            label: "Source",
            dataKey: "source",
            value: ({ source_from_client = null, source }: any) => {
                // if (source_from_client) {
                //     return source_from_client;
                // }

                return source;
            },
            minWidth: 180,
            align: "left",
            resizable: false
        },
        {
            label: "First packet time",
            dataKey: "date_first",
            flexGrow: 1,
            align: "left",
            resizable: false
        },
        {
            label: "Last packet time",
            dataKey: "date_last",
            flexGrow: 1,
            align: "left",
            resizable: false
        },
        {
            label: "Download",
            dataKey: "action",
            width: 145,
            align: "center",
            value: ({ key, original_filename = "file" }: any) => {
                const isDownloading = downloadingKeys.has(key);

                return <>
                    <TableActionButton
                        onClick={async () => {
                            try {
                                setDownloadingKeys(prev => new Set(prev).add(key));

                                const blob = await downloadFile({ key }).unwrap();
                                handleDownload(original_filename, blob);
                            } finally {
                                setDownloadingKeys(prev => {
                                    const next = new Set(prev);
                                    next.delete(key);
                                    return next;
                                });
                            }
                        }}
                        disabled={isDownloading}
                    >
                        <Icon as={Save} />
                        <span>Download</span>
                    </TableActionButton>
                </>;
            }
        }
    ], [downloadingKeys]);

    const [currentFileKey, setCurrentFileKey] = useState(null);
    const [currentFileData, setCurrentFileData] = useState(null);

    const loading = isLoading || isFetching;

    useEffect(() => {
        if (files && files.length) {
            const firstFile = files[0];
            setCurrentFileKey(firstFile.key);
            setCurrentFileData(firstFile);
        } else {
            setCurrentFileKey(null);
            setCurrentFileData(null);
        }
    }, [files]);

    return (
        <PageContainer>
            <FilesFilters
                model={filesFormModel}
                loading={loading}
                filters={filters}
                formError={formError}
                notUpdatableKeys={notUpdatableKeys}
                currentParamsRef={currentParamsRef}
                setFilters={setFilters}
                setFormError={setFormError}
                setRefreshKey={setRefreshKey}
                setSearchParams={setSearchParams}
            />
            <Panel noStyles noEndPadding>
                <DarkTable
                    rowKey="key"
                    data={files}
                    columns={columns}
                    loading={loading}
                    error={error}
                    showSkeleton
                    // showError
                    rowHeight={40}
                    headerHeight={40}
                    height={760}
                    bordered
                    cellBordered
                    virtualized

                    onRowClick={({ key, ...rowData }) => {
                        setCurrentFileKey(key);
                        setCurrentFileData(rowData);
                    }}
                    rowClassName={(props: any, rowIndex: number) => {
                        if (props && props.key === currentFileKey) {
                            return "current_row";
                        }
                        return "";
                    }}
                />
            </Panel>
        </PageContainer>
    );
};

export default FilesPage; 